const turf = require('@turf/turf')
const utmObj = require('utm-latlng')

/**
 * Universal Mergician - UTM-based Universal Grid Generator
 * 
 * Creates precise grid systems using UTM coordinates for accurate spatial analysis.
 * Similar to the interpolation code but as a standalone utility.
 */

class UniversalMergician {
  constructor(decimalPrecision = 8) {
    // eslint-disable-next-line new-cap
    this.utm = new utmObj() // Default Ellipsoid is 'WGS 84'
    this.decimalPrecision = decimalPrecision // Default 8 decimal places for GeoJSON precision
  }

  /**
   * Round coordinates to fixed decimal precision for consistent grid alignment
   * @param {number} value - Coordinate value to round
   * @param {number} precision - Number of decimal places (default: this.decimalPrecision)
   * @returns {number} Rounded coordinate value
   */
  roundCoordinate(value, precision = this.decimalPrecision) {
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision)
  }

  /**
   * Round a coordinate array [lng, lat] to fixed precision
   * @param {Array} coordinates - [longitude, latitude] array
   * @returns {Array} Rounded coordinate array
   */
  roundCoordinates(coordinates) {
    return [
      this.roundCoordinate(coordinates[0]),
      this.roundCoordinate(coordinates[1])
    ]
  }

  /**
   * Detect UTM zones that intersect with a polygon
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @returns {Array} Array of UTM zone objects with zone info and coverage
   */
  detectUTMZones(polygonGeoJson) {
    const bounds = turf.bbox(polygonGeoJson)
    const [minX, minY, maxX, maxY] = bounds
    
    // Sample points around the polygon to detect zones
    const samplePoints = [
      [minX, minY], // SW
      [maxX, minY], // SE  
      [maxX, maxY], // NE
      [minX, maxY], // NW
      [(minX + maxX) / 2, (minY + maxY) / 2] // Center
    ]
    
    const zones = new Map()
    
    samplePoints.forEach(([lng, lat]) => {
      const utm = this.utm.convertLatLngToUtm(lat, lng, 14)
      const zoneKey = `${utm.ZoneNumber}${utm.ZoneLetter}`
      
      if (!zones.has(zoneKey)) {
        zones.set(zoneKey, {
          zoneNumber: utm.ZoneNumber,
          zoneLetter: utm.ZoneLetter,
          zoneKey: zoneKey,
          samplePoints: []
        })
      }
      zones.get(zoneKey).samplePoints.push([lng, lat])
    })
    
    return Array.from(zones.values())
  }

  /**
   * Check if a polygon crosses UTM zone boundaries
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @returns {Object} Zone analysis result
   */
  analyzeUTMZoneCrossing(polygonGeoJson) {
    const zones = this.detectUTMZones(polygonGeoJson)
    
    if (zones.length === 1) {
      return {
        crossesZones: false,
        primaryZone: zones[0],
        zones: zones,
        recommendation: 'single_zone'
      }
    } else if (zones.length === 2) {
      return {
        crossesZones: true,
        primaryZone: zones[0], // Use first zone as primary
        zones: zones,
        recommendation: 'dual_zone',
        warning: 'Polygon crosses UTM zone boundary. Consider using primary zone or splitting polygon.'
      }
    } else {
      return {
        crossesZones: true,
        primaryZone: zones[0], // Use first zone as primary
        zones: zones,
        recommendation: 'multi_zone',
        warning: 'Polygon crosses multiple UTM zones. Consider splitting polygon or using geographic coordinates.'
      }
    }
  }

  /**
   * Generate grid using a specific UTM zone
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters
   * @param {Object} zoneInfo - UTM zone information
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} GeoJSON FeatureCollection of grid points
   */
  generateGridInZone(polygonGeoJson, gridSpacing, zoneInfo, buffer = 0.001) {
    console.log(`Generating grid in UTM Zone ${zoneInfo.zoneKey}...`)
    
    // Create bounding box around polygon
    const bounds = turf.bbox(polygonGeoJson)
    const minX = bounds[0] - buffer
    const minY = bounds[1] - buffer
    const maxX = bounds[2] + buffer
    const maxY = bounds[3] + buffer

    // Convert bounds to UTM using the specified zone
    const utmSW = this.utm.convertLatLngToUtm(minY, minX, 14)
    const utmNE = this.utm.convertLatLngToUtm(maxY, maxX, 14)
    
    // Force the zone to match our target zone
    const targetZoneNum = zoneInfo.zoneNumber
    const targetZoneLetter = zoneInfo.zoneLetter

    console.log(`Target UTM Zone: ${targetZoneNum}${targetZoneLetter}`)
    console.log(`UTM bounds: SW(${utmSW.Easting}, ${utmSW.Northing}) to NE(${utmNE.Easting}, ${utmNE.Northing})`)

    // Round SW coordinates to nearest grid spacing multiple
    const roundedSW = {
      Easting: Math.ceil(utmSW.Easting / gridSpacing) * gridSpacing,
      Northing: Math.ceil(utmSW.Northing / gridSpacing) * gridSpacing
    }

    // Generate grid points
    const grid = { 
      type: 'FeatureCollection', 
      features: [] 
    }

    let pointCount = 0
    for (let northing = roundedSW.Northing; northing < utmNE.Northing; northing += gridSpacing) {
      for (let easting = roundedSW.Easting; easting < utmNE.Easting; easting += gridSpacing) {
        // Convert UTM back to Lat/Lng using the target zone
        const latLng = this.utm.convertUtmToLatLng(easting, northing, targetZoneNum, targetZoneLetter)
        
        // Round coordinates to fixed precision for consistent grid alignment
        const roundedCoords = this.roundCoordinates([latLng.lng, latLng.lat])
        
        grid.features.push({
          type: 'Feature',
          properties: {
            utmEasting: easting,
            utmNorthing: northing,
            utmZone: `${targetZoneNum}${targetZoneLetter}`,
            gridSpacing: gridSpacing,
            decimalPrecision: this.decimalPrecision,
            zoneForced: true
          },
          geometry: {
            type: 'Point',
            coordinates: roundedCoords
          }
        })
        pointCount++
      }
    }

    console.log(`Generated ${pointCount} grid points in zone ${targetZoneNum}${targetZoneLetter}`)

    // Trim grid points to polygon boundary
    const trimmedGrid = turf.pointsWithinPolygon(grid, polygonGeoJson)
    
    console.log(`Trimmed to ${trimmedGrid.features.length} points within polygon`)

    return trimmedGrid
  }

  /**
   * Generate a universal grid within a polygon boundary with automatic UTM zone handling
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters (default: 3)
   * @param {number} buffer - Buffer distance in degrees (default: 0.001)
   * @param {Object} options - Additional options
   * @param {boolean} options.autoHandleZones - Automatically handle zone crossings (default: true)
   * @param {string} options.zoneStrategy - Strategy for multi-zone polygons: 'primary', 'split', 'warn' (default: 'primary')
   * @returns {Object} GeoJSON FeatureCollection of grid points with zone analysis
   */
  generateUniversalGrid(polygonGeoJson, gridSpacing = 3, buffer = 0.001, options = {}) {
    console.log(`Generating universal grid with ${gridSpacing}m spacing...`)
    
    // Validate input
    if (!polygonGeoJson || !polygonGeoJson.geometry) {
      throw new Error('Invalid polygon GeoJSON provided')
    }

    const { autoHandleZones = true, zoneStrategy = 'primary' } = options

    // Analyze UTM zone crossing
    const zoneAnalysis = this.analyzeUTMZoneCrossing(polygonGeoJson)
    
    console.log(`Zone analysis: ${zoneAnalysis.recommendation}`)
    if (zoneAnalysis.warning) {
      console.warn(`⚠️ ${zoneAnalysis.warning}`)
    }

    let result

    if (!zoneAnalysis.crossesZones || !autoHandleZones) {
      // Single zone or manual handling - use original method
      result = this.generateGridInZone(polygonGeoJson, gridSpacing, zoneAnalysis.primaryZone, buffer)
    } else {
      // Multi-zone handling
      switch (zoneStrategy) {
        case 'primary':
          console.log(`Using primary zone: ${zoneAnalysis.primaryZone.zoneKey}`)
          result = this.generateGridInZone(polygonGeoJson, gridSpacing, zoneAnalysis.primaryZone, buffer)
          break
          
        case 'split':
          console.log('Splitting polygon by UTM zones...')
          result = this.generateMultiZoneGrid(polygonGeoJson, gridSpacing, zoneAnalysis.zones, buffer)
          break
          
        case 'warn':
          console.log('Generating grid with zone warnings...')
          result = this.generateGridInZone(polygonGeoJson, gridSpacing, zoneAnalysis.primaryZone, buffer)
          break
          
        default:
          throw new Error(`Unknown zone strategy: ${zoneStrategy}`)
      }
    }

    // Add zone analysis metadata
    result.zoneAnalysis = zoneAnalysis
    result.gridSpacing = gridSpacing
    result.decimalPrecision = this.decimalPrecision

    return result
  }

  /**
   * Generate grid for multiple UTM zones by splitting the polygon
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters
   * @param {Array} zones - Array of UTM zone objects
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} Combined GeoJSON FeatureCollection from all zones
   */
  generateMultiZoneGrid(polygonGeoJson, gridSpacing, zones, buffer = 0.001) {
    console.log(`Generating multi-zone grid for ${zones.length} zones...`)
    
    const allGrids = []
    let totalPoints = 0
    
    zones.forEach((zone, index) => {
      console.log(`Processing zone ${index + 1}/${zones.length}: ${zone.zoneKey}`)
      
      try {
        // Generate grid for this zone
        const zoneGrid = this.generateGridInZone(polygonGeoJson, gridSpacing, zone, buffer)
        
        // Add zone metadata to each feature
        zoneGrid.features.forEach(feature => {
          feature.properties.zoneIndex = index
          feature.properties.zoneKey = zone.zoneKey
          feature.properties.multiZone = true
        })
        
        allGrids.push(zoneGrid)
        totalPoints += zoneGrid.features.length
        
        console.log(`Zone ${zone.zoneKey}: ${zoneGrid.features.length} points`)
      } catch (error) {
        console.error(`Error generating grid for zone ${zone.zoneKey}:`, error)
      }
    })
    
    // Combine all grids
    const combinedGrid = {
      type: 'FeatureCollection',
      features: allGrids.flatMap(grid => grid.features)
    }
    
    console.log(`Multi-zone grid complete: ${totalPoints} total points across ${zones.length} zones`)
    
    return combinedGrid
  }

  /**
   * Generate a universal grid with custom spacing and return both raw and trimmed grids
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} Object containing rawGrid and trimmedGrid
   */
  generateUniversalGridWithRaw(polygonGeoJson, gridSpacing = 3, buffer = 0.001) {
    console.log(`Generating universal grid with raw output (${gridSpacing}m spacing)...`)
    
    // Validate input
    if (!polygonGeoJson || !polygonGeoJson.geometry) {
      throw new Error('Invalid polygon GeoJSON provided')
    }

    // Create bounding box around polygon
    const bounds = turf.bbox(polygonGeoJson)
    const minX = bounds[0] - buffer
    const minY = bounds[1] - buffer
    const maxX = bounds[2] + buffer
    const maxY = bounds[3] + buffer

    // Convert bounds to UTM coordinates
    const utmSW = this.utm.convertLatLngToUtm(minY, minX, 14)
    const utmNE = this.utm.convertLatLngToUtm(maxY, maxX, 14)
    
    const zoneNum = utmSW.ZoneNumber
    const zoneLetter = utmSW.ZoneLetter

    // Round SW coordinates to nearest grid spacing multiple
    const roundedSW = {
      Easting: Math.ceil(utmSW.Easting / gridSpacing) * gridSpacing,
      Northing: Math.ceil(utmSW.Northing / gridSpacing) * gridSpacing
    }

    // Generate raw grid points (before trimming)
    const rawGrid = { 
      type: 'FeatureCollection', 
      features: [] 
    }

    let pointCount = 0
    for (let northing = roundedSW.Northing; northing < utmNE.Northing; northing += gridSpacing) {
      for (let easting = roundedSW.Easting; easting < utmNE.Easting; easting += gridSpacing) {
        const latLng = this.utm.convertUtmToLatLng(easting, northing, zoneNum, zoneLetter)
        
        // Round coordinates to fixed precision for consistent grid alignment
        const roundedCoords = this.roundCoordinates([latLng.lng, latLng.lat])
        
        rawGrid.features.push({
          type: 'Feature',
          properties: {
            utmEasting: easting,
            utmNorthing: northing,
            utmZone: `${zoneNum}${zoneLetter}`,
            gridSpacing: gridSpacing,
            decimalPrecision: this.decimalPrecision
          },
          geometry: {
            type: 'Point',
            coordinates: roundedCoords
          }
        })
        pointCount++
      }
    }

    // Trim grid points to polygon boundary
    const trimmedGrid = turf.pointsWithinPolygon(rawGrid, polygonGeoJson)
    
    console.log(`Generated ${pointCount} raw points, trimmed to ${trimmedGrid.features.length} points within polygon`)

    return {
      rawGrid: rawGrid,
      trimmedGrid: trimmedGrid,
      utmInfo: {
        zone: `${zoneNum}${zoneLetter}`,
        sw: utmSW,
        ne: utmNE,
        roundedSW: roundedSW
      }
    }
  }

  /**
   * Calculate optimal grid spacing based on polygon area and desired point density
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} targetPointsPerAcre - Desired point density (default: 10)
   * @returns {number} Recommended grid spacing in meters
   */
  calculateOptimalGridSpacing(polygonGeoJson, targetPointsPerAcre = 10) {
    const area = turf.area(polygonGeoJson) // Area in square meters
    const areaInAcres = area * 0.000247105 // Convert to acres
    
    const totalTargetPoints = areaInAcres * targetPointsPerAcre
    const gridSpacing = Math.sqrt(area / totalTargetPoints)
    
    // Round to nearest meter
    const roundedSpacing = Math.round(gridSpacing)
    
    console.log(`Area: ${areaInAcres.toFixed(2)} acres`)
    console.log(`Target density: ${targetPointsPerAcre} points/acre`)
    console.log(`Recommended grid spacing: ${roundedSpacing}m`)
    
    return Math.max(1, roundedSpacing) // Minimum 1 meter
  }

  /**
   * Generate grid with automatic spacing based on polygon characteristics
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} targetPointsPerAcre - Desired point density
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} Trimmed grid with optimal spacing
   */
  generateOptimalGrid(polygonGeoJson, targetPointsPerAcre = 10, buffer = 0.001) {
    const optimalSpacing = this.calculateOptimalGridSpacing(polygonGeoJson, targetPointsPerAcre)
    return this.generateUniversalGrid(polygonGeoJson, optimalSpacing, buffer)
  }

  /**
   * Get UTM zone information for a given point
   * @param {number} lng - Longitude
   * @param {number} lat - Latitude
   * @returns {Object} UTM zone information
   */
  getUTMZoneInfo(lng, lat) {
    const utm = this.utm.convertLatLngToUtm(lat, lng, 14)
    return {
      zone: `${utm.ZoneNumber}${utm.ZoneLetter}`,
      easting: utm.Easting,
      northing: utm.Northing,
      zoneNumber: utm.ZoneNumber,
      zoneLetter: utm.ZoneLetter
    }
  }

  /**
   * Convert a point from UTM to Lat/Lng
   * @param {number} easting - UTM Easting
   * @param {number} northing - UTM Northing
   * @param {number} zoneNumber - UTM Zone Number
   * @param {string} zoneLetter - UTM Zone Letter
   * @returns {Object} Lat/Lng coordinates
   */
  utmToLatLng(easting, northing, zoneNumber, zoneLetter) {
    return this.utm.convertUtmToLatLng(easting, northing, zoneNumber, zoneLetter)
  }

  /**
   * Convert a point from Lat/Lng to UTM
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Object} UTM coordinates
   */
  latLngToUTM(lat, lng) {
    return this.utm.convertLatLngToUtm(lat, lng, 14)
  }

  /**
   * Generate a universal grid that aligns across different geometries
   * Uses a global reference point to ensure consistent grid alignment
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters
   * @param {Array} globalReferencePoint - [lng, lat] reference point for alignment (optional)
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} GeoJSON FeatureCollection of aligned grid points
   */
  generateAlignedUniversalGrid(polygonGeoJson, gridSpacing = 3, globalReferencePoint = null, buffer = 0.001) {
    console.log(`Generating aligned universal grid with ${gridSpacing}m spacing...`)
    
    // Validate input
    if (!polygonGeoJson || !polygonGeoJson.geometry) {
      throw new Error('Invalid polygon GeoJSON provided')
    }

    // Use provided reference point or calculate from polygon centroid
    let referencePoint
    if (globalReferencePoint) {
      referencePoint = globalReferencePoint
    } else {
      const centroid = turf.centroid(polygonGeoJson)
      referencePoint = [centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]]
    }

    // Convert reference point to UTM
    const refUTM = this.utm.convertLatLngToUtm(referencePoint[1], referencePoint[0], 14)
    const zoneNum = refUTM.ZoneNumber
    const zoneLetter = refUTM.ZoneLetter

    console.log(`Using reference point: [${referencePoint[0]}, ${referencePoint[1]}]`)
    console.log(`Reference UTM: ${refUTM.Easting}, ${refUTM.Northing} (Zone ${zoneNum}${zoneLetter})`)

    // Create bounding box around polygon
    const bounds = turf.bbox(polygonGeoJson)
    const minX = bounds[0] - buffer
    const minY = bounds[1] - buffer
    const maxX = bounds[2] + buffer
    const maxY = bounds[3] + buffer

    // Convert bounds to UTM
    const utmSW = this.utm.convertLatLngToUtm(minY, minX, 14)
    const utmNE = this.utm.convertLatLngToUtm(maxY, maxX, 14)

    // Calculate grid origin aligned to reference point
    const gridOrigin = {
      Easting: Math.floor(refUTM.Easting / gridSpacing) * gridSpacing,
      Northing: Math.floor(refUTM.Northing / gridSpacing) * gridSpacing
    }

    // Calculate grid bounds aligned to the global grid
    const gridSW = {
      Easting: Math.floor(utmSW.Easting / gridSpacing) * gridSpacing,
      Northing: Math.floor(utmSW.Northing / gridSpacing) * gridSpacing
    }
    const gridNE = {
      Easting: Math.ceil(utmNE.Easting / gridSpacing) * gridSpacing,
      Northing: Math.ceil(utmNE.Northing / gridSpacing) * gridSpacing
    }

    console.log(`Grid origin: ${gridOrigin.Easting}, ${gridOrigin.Northing}`)
    console.log(`Grid bounds: SW(${gridSW.Easting}, ${gridSW.Northing}) to NE(${gridNE.Easting}, ${gridNE.Northing})`)

    // Generate aligned grid points
    const grid = { 
      type: 'FeatureCollection', 
      features: [] 
    }

    let pointCount = 0
    for (let northing = gridSW.Northing; northing <= gridNE.Northing; northing += gridSpacing) {
      for (let easting = gridSW.Easting; easting <= gridNE.Easting; easting += gridSpacing) {
        // Convert UTM back to Lat/Lng
        const latLng = this.utm.convertUtmToLatLng(easting, northing, zoneNum, zoneLetter)
        
        // Round coordinates to fixed precision for consistent grid alignment
        const roundedCoords = this.roundCoordinates([latLng.lng, latLng.lat])
        
        grid.features.push({
          type: 'Feature',
          properties: {
            utmEasting: easting,
            utmNorthing: northing,
            utmZone: `${zoneNum}${zoneLetter}`,
            gridSpacing: gridSpacing,
            decimalPrecision: this.decimalPrecision,
            gridOrigin: [gridOrigin.Easting, gridOrigin.Northing],
            isAligned: true
          },
          geometry: {
            type: 'Point',
            coordinates: roundedCoords
          }
        })
        pointCount++
      }
    }

    console.log(`Generated ${pointCount} aligned grid points`)

    // Trim grid points to polygon boundary
    const trimmedGrid = turf.pointsWithinPolygon(grid, polygonGeoJson)
    
    console.log(`Trimmed to ${trimmedGrid.features.length} points within polygon`)

    return trimmedGrid
  }

  /**
   * Get the global grid origin for a given reference point and grid spacing
   * This ensures all grids with the same reference point and spacing will align
   * @param {Array} referencePoint - [lng, lat] reference point
   * @param {number} gridSpacing - Grid spacing in meters
   * @returns {Object} Grid origin information
   */
  getGlobalGridOrigin(referencePoint, gridSpacing) {
    const refUTM = this.utm.convertLatLngToUtm(referencePoint[1], referencePoint[0], 14)
    
    const gridOrigin = {
      Easting: Math.floor(refUTM.Easting / gridSpacing) * gridSpacing,
      Northing: Math.floor(refUTM.Northing / gridSpacing) * gridSpacing,
      Zone: `${refUTM.ZoneNumber}${refUTM.ZoneLetter}`,
      gridSpacing: gridSpacing,
      referencePoint: referencePoint
    }

    return gridOrigin
  }

  /**
   * Handle extreme UTM zone intersection cases (4-zone corners)
   * @param {Object} polygonGeoJson - GeoJSON polygon feature
   * @param {number} gridSpacing - Grid spacing in meters
   * @param {number} buffer - Buffer distance in degrees
   * @returns {Object} GeoJSON FeatureCollection with special handling
   */
  handleExtremeZoneIntersection(polygonGeoJson, gridSpacing = 3, buffer = 0.001) {
    console.log('🚨 Handling extreme UTM zone intersection (4-zone corner)...')
    
    const zoneAnalysis = this.analyzeUTMZoneCrossing(polygonGeoJson)
    
    if (zoneAnalysis.zones.length < 4) {
      console.log('Not a 4-zone intersection, using standard multi-zone handling')
      return this.generateMultiZoneGrid(polygonGeoJson, gridSpacing, zoneAnalysis.zones, buffer)
    }
    
    console.log(`Detected ${zoneAnalysis.zones.length} UTM zones - extreme intersection case`)
    
    // For 4-zone intersections, use the zone with the most coverage
    const primaryZone = zoneAnalysis.zones[0] // Use first zone as primary
    
    console.log(`Using primary zone ${primaryZone.zoneKey} for extreme intersection`)
    console.log('⚠️ Warning: Grid may have coordinate discontinuities at zone boundaries')
    
    const result = this.generateGridInZone(polygonGeoJson, gridSpacing, primaryZone, buffer)
    
    // Add extreme intersection metadata
    result.zoneAnalysis = {
      ...zoneAnalysis,
      extremeIntersection: true,
      handling: 'primary_zone_forced',
      warning: 'Extreme UTM zone intersection detected. Grid uses primary zone with potential coordinate discontinuities.'
    }
    
    return result
  }

  /**
   * Get UTM zone boundary information for a given area
   * @param {Array} bounds - [minX, minY, maxX, maxY] bounding box
   * @returns {Object} Zone boundary analysis
   */
  getUTMZoneBoundaries(bounds) {
    const [minX, minY, maxX, maxY] = bounds
    
    // Sample points around the boundary
    const samplePoints = [
      [minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], // Corners
      [(minX + maxX) / 2, minY], [maxX, (minY + maxY) / 2], // Mid-edges
      [(minX + maxX) / 2, maxY], [minX, (minY + maxY) / 2],
      [(minX + maxX) / 2, (minY + maxY) / 2] // Center
    ]
    
    const zones = new Map()
    
    samplePoints.forEach(([lng, lat]) => {
      const utm = this.utm.convertLatLngToUtm(lat, lng, 14)
      const zoneKey = `${utm.ZoneNumber}${utm.ZoneLetter}`
      
      if (!zones.has(zoneKey)) {
        zones.set(zoneKey, {
          zoneNumber: utm.ZoneNumber,
          zoneLetter: utm.ZoneLetter,
          zoneKey: zoneKey,
          samplePoints: []
        })
      }
      zones.get(zoneKey).samplePoints.push([lng, lat])
    })
    
    return {
      zones: Array.from(zones.values()),
      zoneCount: zones.size,
      isMultiZone: zones.size > 1,
      isExtremeIntersection: zones.size >= 4
    }
  }
}

// Export the class and a default instance
module.exports = {
  UniversalMergician,
  // Default instance for convenience
  universalMergician: new UniversalMergician()
}

// Example usage function (for testing)
// eslint-disable-next-line no-unused-vars
function exampleUsage () {
  const { universalMergician } = require('./universalMergician')
  
  // Example polygon (you would get this from your blocks or dataset hull)
  const examplePolygon = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-122.5, 37.7],
        [-122.4, 37.7],
        [-122.4, 37.8],
        [-122.5, 37.8],
        [-122.5, 37.7]
      ]]
    },
    properties: {}
  }
  
  // Example polygon that crosses UTM zone boundary (hypothetical)
  const zoneCrossingPolygon = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-120.0, 35.0], // Near UTM zone boundary
        [-119.5, 35.0],
        [-119.5, 35.5],
        [-120.0, 35.5],
        [-120.0, 35.0]
      ]]
    },
    properties: {}
  }
  
  // Global reference point for consistent alignment across geometries
  const globalReferencePoint = [-122.45, 37.75]
  
  console.log('=== Standard Grid Generation ===')
  // Generate a 10-meter grid with automatic UTM zone handling
  const grid = universalMergician.generateUniversalGrid(examplePolygon, 10)
  console.log(`Generated grid with ${grid.features.length} points`)
  console.log('Zone analysis:', grid.zoneAnalysis)
  
  console.log('\n=== UTM Zone Analysis ===')
  // Analyze UTM zone crossing
  const zoneAnalysis = universalMergician.analyzeUTMZoneCrossing(examplePolygon)
  console.log('Zone analysis result:', zoneAnalysis)
  
  console.log('\n=== Zone Crossing Handling ===')
  // Test with zone crossing polygon
  const crossingGrid = universalMergician.generateUniversalGrid(zoneCrossingPolygon, 10, 0.001, {
    zoneStrategy: 'primary' // Options: 'primary', 'split', 'warn'
  })
  console.log(`Zone crossing grid: ${crossingGrid.features.length} points`)
  console.log('Crossing zone analysis:', crossingGrid.zoneAnalysis)
  
  console.log('\n=== Multi-Zone Grid Generation ===')
  // Generate multi-zone grid
  const multiZoneGrid = universalMergician.generateUniversalGrid(zoneCrossingPolygon, 10, 0.001, {
    zoneStrategy: 'split'
  })
  console.log(`Multi-zone grid: ${multiZoneGrid.features.length} points`)
  
  console.log('\n=== Extreme Zone Intersection ===')
  // Handle extreme 4-zone intersection
  const extremeGrid = universalMergician.handleExtremeZoneIntersection(zoneCrossingPolygon, 10)
  console.log(`Extreme intersection grid: ${extremeGrid.features.length} points`)
  console.log('Extreme intersection analysis:', extremeGrid.zoneAnalysis)
  
  console.log('\n=== Aligned Grid Generation ===')
  // Generate aligned grid that will match other geometries
  const alignedGrid = universalMergician.generateAlignedUniversalGrid(examplePolygon, 10, globalReferencePoint)
  console.log(`Generated aligned grid with ${alignedGrid.features.length} points`)
  console.log(`Grid origin: [${alignedGrid.features[0].properties.gridOrigin[0]}, ${alignedGrid.features[0].properties.gridOrigin[1]}]`)
  
  console.log('\n=== Optimal Grid Generation ===')
  // Generate optimal grid based on area
  const optimalGrid = universalMergician.generateOptimalGrid(examplePolygon, 5) // 5 points per acre
  console.log(`Generated optimal grid with ${optimalGrid.features.length} points`)
  
  console.log('\n=== Global Grid Origin ===')
  // Get global grid origin for consistent alignment
  const gridOrigin = universalMergician.getGlobalGridOrigin(globalReferencePoint, 10)
  console.log('Global grid origin:', gridOrigin)
  
  return { 
    grid, 
    zoneAnalysis, 
    crossingGrid, 
    multiZoneGrid, 
    extremeGrid,
    alignedGrid, 
    optimalGrid, 
    gridOrigin 
  }
}

// Uncomment to test
// exampleUsage()
